import { ColumnDef } from '@tanstack/react-table'
import { ChainsSummary } from 'models/summaries'
import { Preferences } from 'models/preferences'
import { SortOption } from 'components/Buttons/TableButtons/TableButtonSort'
import { ReactElement } from 'react'
import { BsXCircle } from 'react-icons/bs'
import { PiXCircle } from 'react-icons/pi'
import useApiService from 'services/api'
import { DataTableColumnSort } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import {
  sortableFieldOptions as chainsSortableFieldOptions,
  sortableFields as chainsSortableFields,
  useChainsTableColumns,
} from './ChainsTableColumns'
import {
  sortableFieldOptions as chainsReportsSortableFieldOptions,
  sortableFields as chainsReportsSortableFields,
  useChainsTableColumnsV2,
} from './ChainsTableColumnsV2'

type ApiService = ReturnType<typeof useApiService>

type ChainsListAction = (
  params: Record<string, any>,
  signal?: AbortSignal
) => Promise<{ results: any[]; count?: number }>

/**
 * The axes on which the two restaurant/Chains table variants differ
 * (FIR-7728, step 1 — collapse `ChainsTableComponent` V1 + V2). Everything else
 * — the tri-mode elastic search, the `setChainsSummary` header, the menu modal,
 * the product selector, the location columns/filters, and the full action set —
 * is identical and lives once in the shared `<ChainsTableComponent>`.
 *
 *   - V1 `restaurants-bars`     → `ChainProxy`,        `getChainsListV2`
 *   - V2 `chains-reports-table` → `ChainProxyReport`,  `getChainsWithCRMReports`
 *
 * (Note: `companyTypeSlug` stays `'restaurants-bars'` for BOTH variants — only
 * the `tableTypeSlug` and the data source change.)
 */
export interface ChainsTableVariant {
  /** tableType slug for tags/distributors/GPOs/campaign/export/deal actions. */
  tableTypeSlug: 'restaurants-bars' | 'chains-reports-table'
  /** Per-variant columns hook (V1 keys `ChainProxy`, V2 keys `ChainProxyReport`). */
  useColumns: (
    chainsSummary: ChainsSummary | undefined,
    setMenuModal: any,
    sorting: DataTableColumnSort[] | undefined,
    data: any,
    selectedProducts: number[],
    preferences: Preferences | undefined,
    inCampaigns?: boolean
  ) => ColumnDef<any, any>[]
  sortableFields: readonly string[]
  sortableFieldOptions: readonly SortOption<string>[]
  /** Resolve the list endpoint off the api service (not a hook). */
  getListAction: (api: ApiService) => ChainsListAction
  /**
   * Forward the previous summary (with `isLoading: true`) during a refetch to
   * avoid a flicker (V2). V1 simply skips the update while loading.
   */
  preserveSummaryWhileLoading: boolean
  /** Icon for the "Unassign from Campaign" action. */
  unassignIcon: ReactElement
}

/** V1 — the original `restaurants-bars` chains table. */
export const chainsTableVariant: ChainsTableVariant = {
  tableTypeSlug: 'restaurants-bars',
  useColumns: useChainsTableColumns,
  sortableFields: chainsSortableFields,
  sortableFieldOptions: chainsSortableFieldOptions,
  getListAction: (api) => api.getChainsListV2,
  preserveSummaryWhileLoading: false,
  unassignIcon: <BsXCircle size={20} />,
}

/** V2 — the CRM-reports chains table (`enabledReportChains`). */
export const chainsReportsTableVariant: ChainsTableVariant = {
  tableTypeSlug: 'chains-reports-table',
  useColumns: useChainsTableColumnsV2,
  sortableFields: chainsReportsSortableFields,
  sortableFieldOptions: chainsReportsSortableFieldOptions,
  getListAction: (api) => api.getChainsWithCRMReports,
  preserveSummaryWhileLoading: true,
  unassignIcon: <PiXCircle size={20} />,
}
